<template>
 
  <div class="">

    <div class="w-[500px] bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-2">
          <h1 class="text-lg font-semibold text-gray-900">My Todos</h1>
          <span class="bg-gray-100 text-gray-600 text-sm px-2 py-1 rounded-full">
            {{ todos?.length }}
          </span>
        </div>
        <button
          @click="markSelectedAsDone"
          :disabled="selectedTodos.length === 0 || marking"
          class="bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
        >
          Mark as done
        </button>
      </div>

      <!-- list -->
      <div class="space-y-4 max-h-[400px] overflow-y-auto pr-1">
        <div
          v-for="todo in todos" 
          :key="todo.id"
          class="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <!-- Checkbox -->
          <div class="flex items-center mt-1">
            <input
              :id="`todo-${todo.id}`"
              :name="`todo-${todo.id}`"
              v-model="selectedTodos"
              :value="todo.id"
              type="checkbox"
              class="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 focus:ring-2 disabled:opacity-50 cursor-pointer"
            />
          </div>
  
          <!-- Content -->
          <div class="flex-1 min-w-0 cursor-pointer" @click="openTodo(todo)">
            <div class="flex items-center gap-2 mb-1">
              <h3 class="font-medium text-sm text-gray-900">
                {{ todo.title }}
              </h3>
              <span class="text-xs text-gray-500">•</span>
              <span class="text-xs text-gray-500">
                <!-- Due: {{ formatDate(todo?.dueDate) }} -->
              </span>
            </div>
            <p class="text-sm text-gray-600">
              {{ todo.content }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, defineProps, defineEmits } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import Api from '@/services/api'

interface TodoAdditionalData {
  year: number
  month: number
  project_id: number
  cost_center: string
  project_title: string
  foreign_establishment_id: number
}

interface Todo {
  id: number
  user: string
  title: string
  content: string
  item_id: number
  completed: boolean
  link_to_action: string
  category: string
  additional_data: TodoAdditionalData
  due_date: string | null
  created_at: string
}

defineProps<{
  todos: Todo[]
}>()

const emit = defineEmits<{
  'show-alert': [payload: { type: string; title: string; state: boolean }]
}>()

const router = useRouter()
const store = useStore()
const selectedTodos = ref<number[]>([])
const marking = ref(false)

const todoActionHandlers: Record<string, (todo: Todo) => void> = {
  '/edit-pm-local': (todo) => {
    const projectTitle = [todo.additional_data?.cost_center, todo.additional_data?.project_title]?.filter(Boolean)?.join(" ")
    store.commit("pmLocalModules/SET_PM_LOCAL_TITLE_STATE", projectTitle);
    if (todo.title === 'Month Locked – Review Required' || todo.title === 'Month Locked - Signature Required') {
      store.commit('pmLocalModules/SET_FOREIGN_ESTABLISH_ID_TO_STATE', todo.additional_data?.foreign_establishment_id)
      store.commit('pmLocalModules/SET_PROJECT_ID_TO_STATE', todo.additional_data?.project_id)
      store.commit('expenseModules/SET_PDF_MONTH_TO_STATE', todo.additional_data?.month)
      store.commit('expenseModules/SET_PDF_YEAR_TO_STATE', todo.additional_data?.year)
      store.commit('expenseModules/SET_PDF_EXPENSE_TYPE_TO_STATE', 'cash')
      router.push({ name: 'viewExpensePdf' })
    } else {
      store.commit('pmLocalModules/SET_PM_LOCAL_ID_STATE', todo.item_id)
      router.push({ name: 'editPmLocal' })
    }
  },
}

function openTodo(todo: Todo) {
  const handler = todoActionHandlers[todo.link_to_action]
  if (handler) handler(todo)
}

async function markSelectedAsDone() {
  if (selectedTodos.value.length === 0) {
    emit('show-alert', { type: 'error', title: 'Please select at least one todo.', state: true })
    return
  }
  marking.value = true
  try {
    const response = await Api().put('/hr/to-dos/mark-as-completed/', {
      ids: selectedTodos.value,
      completed: true,
    })
    if (response.data.status === 'success') {
      emit('show-alert', { type: 'success', title: response.data?.detail, state: true })
      // store.dispatch('auth/getUserHomePage')
      selectedTodos.value = []
    }
  } catch (error: any) {
    emit('show-alert', { type: 'error', title: error?.response?.data?.detail, state: true })
  } finally {
    marking.value = false
  }
}
</script>
